import { useEffect, useRef, useState } from 'react';
import styles from './Hero.module.css';

const Hero = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Choix aléatoire entre 3 variantes : 0=video, 1=pluie, 2=braises
  const [heroVariant] = useState(() => Math.floor(Math.random() * 3));

  useEffect(() => {
    // Ne lancer l'animation que si ce n'est pas la vidéo (variantes 1 ou 2)
    if (heroVariant === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Position de la souris
    const mouse = { x: -1000, y: -1000 };
    const mouseRadius = 120; // Rayon modéré pour contournement naturel

    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    window.addEventListener('mousemove', handleMouseMove);

    if (heroVariant === 1) {
      // VARIANTE 1 : Pluie inversée
      class RainDrop {
        x: number;
        y: number;
        length: number;
        speed: number;
        opacity: number;

        constructor() {
          this.x = Math.random() * canvas!.width;
          this.y = Math.random() * canvas!.height + canvas!.height;
          this.length = Math.random() * 15 + 10;
          this.speed = Math.random() * 3 + 2;
          this.opacity = Math.random() * 0.3 + 0.2;
        }

        update() {
          this.y -= this.speed;
          if (this.y < -this.length) {
            this.y = canvas!.height + this.length;
            this.x = Math.random() * canvas!.width;
          }
        }

        draw() {
          ctx!.strokeStyle = `rgba(0, 255, 157, ${this.opacity})`;
          ctx!.lineWidth = 1.5;
          ctx!.beginPath();
          ctx!.moveTo(this.x, this.y);
          ctx!.lineTo(this.x, this.y + this.length);
          ctx!.stroke();
        }
      }

      const rainDrops: RainDrop[] = [];
      for (let i = 0; i < 50; i++) {
        rainDrops.push(new RainDrop());
      }

      const animate = () => {
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#0a0e0d');
        gradient.addColorStop(1, '#121817');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        rainDrops.forEach(drop => {
          drop.update();
          drop.draw();
        });

        requestAnimationFrame(animate);
      };
      animate();
    } else {
      // VARIANTE 2 : Braises volantes
      class Ember {
        x: number;
        y: number;
        baseSpeedY: number;
        speedX: number;
        speedY: number;
        size: number;
        opacity: number;
        flicker: number;
        flickerSpeed: number;

        constructor() {
          this.x = Math.random() * canvas!.width;
          this.y = canvas!.height + Math.random() * 100;
          this.baseSpeedY = -(Math.random() * 1.5 + 0.5);
          this.speedX = (Math.random() - 0.5) * 1;
          this.speedY = this.baseSpeedY;
          this.size = Math.random() * 3 + 2;
          this.opacity = Math.random() * 0.6 + 0.4;
          this.flicker = 0;
          this.flickerSpeed = Math.random() * 0.1 + 0.05;
        }

        update() {
          // Mouvement aléatoire léger
          this.speedX += (Math.random() - 0.5) * 0.15;
          this.speedX *= 0.97; // Amortissement

          // Contournement de la souris (pas de fuite)
          const dx = this.x - mouse.x;
          const dy = this.y - mouse.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < mouseRadius && distance > 0) {
            // Contournement latéral progressif
            const angle = Math.atan2(dy, dx);
            const force = (mouseRadius - distance) / mouseRadius;

            // Force modérée pour contourner sans paniquer
            this.speedX += Math.cos(angle) * force * 2;
            this.speedY += Math.sin(angle) * force * 1.5;
          }

          // Forte tendance à monter
          this.speedY = this.speedY * 0.92 + this.baseSpeedY * 0.08;

          this.x += this.speedX;
          this.y += this.speedY;

          // Réapparition
          if (this.y < -10) {
            this.y = canvas!.height + 10;
            this.x = Math.random() * canvas!.width;
            this.speedX = (Math.random() - 0.5) * 0.5;
            this.speedY = this.baseSpeedY;
          }
          if (this.x < 0) this.x = canvas!.width;
          if (this.x > canvas!.width) this.x = 0;

          // Scintillement
          this.flicker += this.flickerSpeed;
        }

        draw() {
          const flickerOpacity = this.opacity * (0.7 + Math.sin(this.flicker) * 0.3);

          // Lueur verte
          const gradient = ctx!.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * 3);
          gradient.addColorStop(0, `rgba(0, 255, 157, ${flickerOpacity * 0.8})`);
          gradient.addColorStop(0.5, `rgba(46, 139, 87, ${flickerOpacity * 0.5})`);
          gradient.addColorStop(1, 'rgba(0, 255, 157, 0)');
          ctx!.fillStyle = gradient;
          ctx!.beginPath();
          ctx!.arc(this.x, this.y, this.size * 3, 0, Math.PI * 2);
          ctx!.fill();

          // Cœur de la braise vert clair
          ctx!.fillStyle = `rgba(180, 255, 200, ${flickerOpacity})`;
          ctx!.beginPath();
          ctx!.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx!.fill();
        }
      }

      const embers: Ember[] = [];
      for (let i = 0; i < 60; i++) {
        embers.push(new Ember());
      }

      const animate = () => {
        // Fond sombre avec teinte verte
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#0a0e0d');
        gradient.addColorStop(1, '#121817');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        embers.forEach(ember => {
          ember.update();
          ember.draw();
        });

        requestAnimationFrame(animate);
      };
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [heroVariant]);

  return (
    <section id="accueil" className={styles.hero}>
      {heroVariant === 0 ? (
        <>
          {/* Variante 0: Video */}
          <video
            className={styles.heroVideo}
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/videos/hero-background.mp4" type="video/mp4" />
          </video>
          <div className={styles.videoOverlay}></div>
        </>
      ) : (
        <>
          {/* Variante 1: Pluie ou Variante 2: Braises */}
          <canvas ref={canvasRef} className={styles.heroCanvas}></canvas>
        </>
      )}

      <div className={`container ${styles.heroContent}`}>
        <div className={styles.heroText}>
          <h1 className={styles.heroTitle}>
            Des sites web <span className="text-gradient">performants</span>,{' '}
            <span className="text-gradient">rapides</span> et{' '}
            <span className="text-gradient">responsables</span>
          </h1>

          <p className={styles.heroSubtitle}>
            Création web, SEO, maintenance et marketing digital pour développer votre présence en ligne
          </p>

          <div className={styles.heroCta}>
            <a href="#services" className="btn btn-primary">
              Découvrir mes services
            </a>
            <a href="#contact" className="btn btn-secondary">
              Demander un devis
            </a>
          </div>
        </div>

        <div className={styles.heroVisual}>
          {/* Visual element removed */}
        </div>
      </div>

      <div className={styles.scrollIndicator}>
        <span>Scroll</span>
        <div className={styles.scrollLine}></div>
      </div>
    </section>
  );
};

export default Hero;
